/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@explorer/core';
import {
  ControlEscalationPriority,
  ControlUserContactInfoResponse,
} from '@explorer/open-api-client';
import { ContactInfo } from '@explorer/open-api-client/models/ContactInfo';
import AddIcon from '@mui/icons-material/Add';
import { useIntl } from 'react-intl';

import { Stack, Table, useDialog } from '@crucible-risk/security-applications';

import { ContactModal, ContactModalMode } from './ContactModal';
import { EscalationContactRow, GetColumns } from './getColumns';
import { useMutationUpdateEscalationClientContacts } from './useMutationUpdateEscalationClientContacts';

interface EscalationContactsModalProps {
  clientId: string;
  open: boolean;
  closeDialog: () => void;
  contacts: ControlUserContactInfoResponse[];
}

export const EscalationContactsModal = ({
  clientId,
  open,
  closeDialog,
  contacts,
}: EscalationContactsModalProps) => {
  const intl = useIntl();

  const {
    openDialog: openContactModal,
    closeDialog: closeContactModal,
    open: contactModalOpen,
  } = useDialog();

  const [contactModalMode, setContactModalMode] = useState<ContactModalMode>(
    ContactModalMode.ADD,
  );
  const [selectedContact, setSelectedContact] =
    useState<ControlUserContactInfoResponse | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { mutate: updateContact, isLoading } =
    useMutationUpdateEscalationClientContacts({
      client_id: clientId,
      onSuccess: () => {
        closeContactModal();
        setSelectedContact(null);
      },
    });

  const handleAddContact = (contactInfo: ContactInfo) => {
    updateContact(contactInfo);
  };

  const handleEditContact = (contact: ControlUserContactInfoResponse) => {
    setSelectedContact(contact);
    setContactModalMode(ContactModalMode.EDIT);
    openContactModal();
  };

  const handleSaveEdit = (contactInfo: ContactInfo) => {
    updateContact(contactInfo);
  };

  const handleContactModalClose = () => {
    closeContactModal();
    if (contactModalMode === ContactModalMode.EDIT) {
      setSelectedContact(null);
    }
  };

  const handleClose = () => {
    closeContactModal();
    setSelectedContact(null);
    closeDialog();
  };

  const handleOpenAddModal = () => {
    setContactModalMode(ContactModalMode.ADD);
    openContactModal();
  };

  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      const aIsActive =
        a.escalation_priority === ControlEscalationPriority.TierOneAllAlerts;
      const bIsActive =
        b.escalation_priority === ControlEscalationPriority.TierOneAllAlerts;

      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      // If both have same active status, sort by email alphabetically
      return (a.email || '').localeCompare(b.email || '');
    });
  }, [contacts]);

  const rows: EscalationContactRow[] = sortedContacts.map((contact) => ({
    id: contact.email || contact.phone || Math.random().toString(),
    email: contact.email || '-',
    first_name: contact.first_name || '-',
    last_name: contact.last_name || '-',
    phone: contact.phone || '-',
    escalation_priority:
      contact.escalation_priority || ControlEscalationPriority.None,
    contact,
  }));

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {intl.formatMessage({
                id: 'mdr.escalation-contacts.modal.title',
              })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: 'mdr.escalation-contacts.modal.add-contact-button',
              })}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {sortedContacts.length > 0 ? (
              <Box>
                <Table
                  rows={rows}
                  paginationMode="client"
                  page={page - 1}
                  setPage={(newPage) => setPage(newPage + 1)}
                  pageSize={pageSize}
                  rowCount={rows?.length ?? 0}
                  setPageSize={(newPageSize) => setPageSize(newPageSize)}
                  hasPagination
                  loading={isLoading}
                  error={false}
                  columns={GetColumns({
                    onEditContact: handleEditContact,
                    isLoading,
                  })}
                  renderEmptyText=""
                />
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" color="text.secondary">
                  {intl.formatMessage({
                    id: 'mdr.escalation-contacts.no-contacts',
                  })}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            {intl.formatMessage({ id: 'global.cancel' })}
          </Button>
        </DialogActions>
      </Dialog>
      <ContactModal
        mode={contactModalMode}
        open={contactModalOpen}
        onClose={handleContactModalClose}
        contact={
          contactModalMode === ContactModalMode.EDIT
            ? selectedContact
            : undefined
        }
        onSave={
          contactModalMode === ContactModalMode.ADD
            ? handleAddContact
            : handleSaveEdit
        }
        isLoading={isLoading}
      />
    </>
  );
};
